import { cn } from '@lfg/ui/lib';
import { Stack, StackProps, Typography } from '@mui/material';
import { InputHTMLAttributes } from 'react';
import { Control, Controller, FieldValues, Path, RegisterOptions } from 'react-hook-form';

import { gotham } from '@/assets/fonts';

interface InputProps<T extends FieldValues> {
  title?: string;
  stackProps?: StackProps;
  name: Path<T>;
  control: Control<T>;
  rules?: RegisterOptions<T>;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
}

export function Input<T extends FieldValues>({
  title = 'Title',
  className,
  style,
  stackProps,
  name,
  control,
  rules,
  placeholder = 'Text',
  ...inputProps
}: InputProps<T> & InputHTMLAttributes<HTMLInputElement>) {
  // Common styles shared between input and textarea
  const commonStyles = {
    fontFamily: gotham.style.fontFamily,
    ...style,
  };

  // Base classes shared between input and textarea
  const baseClassName =
    'w-full bg-[#FFFFFF05] border p-2 text-white placeholder-[#FFFFFF4D] focus:outline-none focus:ring-1 focus:ring-purple-500';

  return (
    <Stack width={'100%'} gap={'12px'} {...stackProps}>
      <Typography fontSize={{ xs: '14px', sm: '18px' }} variant={'bodyLarge'} color={'#FFFFFF'}>
        {title}
      </Typography>

      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field, fieldState: { error } }) => (
          <>
            <input
              type='text'
              className={cn(
                baseClassName,
                'h-[48px] text-[14px] sm:h-[52px] sm:text-[18px]',
                error ? 'border-red-500' : 'border-[#FFFFFF0D]',
                className,
              )}
              placeholder={placeholder}
              style={commonStyles}
              {...field}
              {...inputProps}
            />
            {error && (
              <Typography color='error' fontSize={{ xs: '12px', sm: '14px' }}>
                {error.message}
              </Typography>
            )}
          </>
        )}
      />
    </Stack>
  );
}
